/**
 * ScamBlocker AI — Sistem Durumu Sayfası (/status)
 * Backend health, AI pipeline durumu, bağlantı testleri.
 */

import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/shared/lib/api/client';
import styles from './StatusPage.module.css';

interface ServiceStatus {
  name: string;
  status: 'ok' | 'degraded' | 'down';
  latency_ms?: number;
  message?: string;
  checked_at: string;
}

interface HealthResponse {
  status: 'ok' | 'degraded' | 'down';
  version?: string;
  uptime_seconds?: number;
  services: ServiceStatus[];
}

const STATUS_ICON = {
  ok: <CheckCircle size={18} color="var(--color-success)" />,
  degraded: <AlertTriangle size={18} color="var(--color-warning)" />,
  down: <XCircle size={18} color="var(--color-danger)" />,
};

const STATUS_COLOR: Record<string, string> = {
  ok: 'var(--color-success)',
  degraded: 'var(--color-warning)',
  down: 'var(--color-danger)',
};

export function StatusPage() {
  const { t } = useTranslation();

  const formatUptime = (secs?: number): string => {
    if (!secs) return '—';
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (d > 0) return t('status.uptimeDaysHours', { days: d, hours: h });
    if (h > 0) return t('status.uptimeHoursMinutes', { hours: h, minutes: m });
    return t('status.uptimeMinutes', { minutes: m });
  };

  const STATUS_LABEL: Record<string, string> = {
    ok: t('status.statusOk'),
    degraded: t('status.statusDegraded'),
    down: t('status.statusDown'),
  };

  const { data, isLoading, isError, refetch, isFetching } = useQuery<HealthResponse>({
    queryKey: ['health'],
    queryFn: async () => {
      const { data } = await apiClient.get('/health');
      return data;
    },
    refetchInterval: 30_000,
  });

  const overall = data?.status ?? (isError ? 'down' : 'ok');

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Activity size={22} className={styles.titleIcon} />
          <h1 className={styles.title}>{t('status.title')}</h1>
        </div>
        <button
          type="button"
          className={`${styles.refreshBtn} ${isFetching ? styles.spinning : ''}`}
          onClick={() => refetch()}
          title={t('status.refresh')}
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Genel durum banner */}
      {!isLoading && (
        <div
          className={styles.overallBanner}
          style={{
            borderColor: STATUS_COLOR[overall],
            background: `${STATUS_COLOR[overall]}15`,
          }}
        >
          {STATUS_ICON[overall as keyof typeof STATUS_ICON]}
          <div>
            <span className={styles.overallLabel} style={{ color: STATUS_COLOR[overall] }}>
          {overall === 'ok'
                ? t('status.statusOk')
                : overall === 'degraded'
                  ? t('status.statusDegraded')
                  : t('status.statusDown')}
            </span>
            {data?.uptime_seconds !== undefined && (
              <span className={styles.uptimeTxt}>
                {t('status.uptimeLabel', { value: formatUptime(data.uptime_seconds) })}
              </span>
            )}
          </div>
          {data?.version && <code className={styles.versionTag}>v{data.version}</code>}
        </div>
      )}

      {isLoading && (
        <div className={styles.center}>
          <span className={styles.spinner} />
        </div>
      )}

      {isError && <div className={styles.errorBox}>{t('status.errorFetch')}</div>}

      {/* Servis listesi */}
      {data?.services && data.services.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('status.servicesTitle')}</h2>
          <div className={styles.serviceList}>
            {data.services.map((svc) => (
              <div key={svc.name} className={styles.serviceRow}>
                <div className={styles.svcLeft}>
                  {STATUS_ICON[svc.status as keyof typeof STATUS_ICON]}
                  <div>
                    <span className={styles.svcName}>{svc.name}</span>
                    {svc.message && <span className={styles.svcMsg}>{svc.message}</span>}
                  </div>
                </div>
                <div className={styles.svcRight}>
                  <span className={styles.svcStatus} style={{ color: STATUS_COLOR[svc.status] }}>
                    {STATUS_LABEL[svc.status] ?? svc.status}
                  </span>
                  {svc.latency_ms !== undefined && (
                    <code className={styles.latency}>{svc.latency_ms}ms</code>
                  )}
                  <span className={styles.checkedAt}>
                    {new Date(svc.checked_at).toLocaleTimeString('tr-TR')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Otomatik yenileme notu */}
      <p className={styles.autoNote}>{t('status.autoRefreshNote')}</p>
    </div>
  );
}
